import { useEffect, useId, useState, type JSX } from 'react'

export interface DurationInputProps {
  /** Total duration in minutes. */
  value: number
  /** Called with the new total minutes whenever hours or minutes change. */
  onChange: (totalMinutes: number) => void
  /** Optional minimum total minutes (clamped). */
  min?: number
  /** Optional maximum total minutes (clamped). */
  max?: number
  disabled?: boolean
}

function clamp(n: number, min: number | undefined, max: number | undefined): number {
  let out = n
  if (min !== undefined && out < min) out = min
  if (max !== undefined && out > max) out = max
  return out
}

/**
 * Minutes-backed duration field rendered as two numeric inputs (Hours +
 * Minutes). Emits the clamped total minutes on every change. Internal string
 * state tracks the raw input so partial edits (clear-then-type) behave.
 */
export default function DurationInput({
  value,
  onChange,
  min,
  max,
  disabled,
}: DurationInputProps): JSX.Element {
  const [hStr, setHStr] = useState(String(Math.floor(value / 60)))
  const [mStr, setMStr] = useState(String(value % 60))
  const hId = useId()
  const mId = useId()

  // Resync when the parent supplies a new value (e.g. editing a different row).
  useEffect(() => {
    setHStr(String(Math.floor(value / 60)))
    setMStr(String(value % 60))
  }, [value])

  function emit(nextH: string, nextM: string): void {
    const h = nextH === '' ? 0 : Number(nextH)
    const m = nextM === '' ? 0 : Number(nextM)
    const safeH = Number.isFinite(h) ? h : 0
    const safeM = Number.isFinite(m) ? m : 0
    onChange(clamp(safeH * 60 + safeM, min, max))
  }

  return (
    <div className="flex items-end gap-3">
      <div className="flex flex-col gap-1">
        <label htmlFor={hId} className="text-xs font-medium text-gray-600">
          Hours
        </label>
        <input
          id={hId}
          aria-label="Hours"
          type="number"
          min={0}
          disabled={disabled}
          className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
          value={hStr}
          onChange={(e) => {
            setHStr(e.target.value)
            emit(e.target.value, mStr)
          }}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={mId} className="text-xs font-medium text-gray-600">
          Minutes
        </label>
        <input
          id={mId}
          aria-label="Minutes"
          type="number"
          min={0}
          disabled={disabled}
          className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
          value={mStr}
          onChange={(e) => {
            setMStr(e.target.value)
            emit(hStr, e.target.value)
          }}
        />
      </div>
    </div>
  )
}
